<template>
    <div class="modal fade" id="createSigningFromOrgDocumentsModal" tabindex="-1" role="dialog" aria-labelledby="createSigningModalLabel" aria-hidden="true" data-backdrop="static">
        <div class="modal-dialog modal-xl" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="createSigningModalLabel">{{ $t('signing.new') }} {{ $tc('signing.pageTitle', 1) }}</h5>
                    <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="loader" v-if="isSaving || isLoadingTemplate"></div>
                <form @submit="onSubmit" v-show="!isSaving" :class="{ 'form-disabled': isLoadingTemplate }">
                    <div class="modal-body" v-if="showEditor">
                        <div class="form-group">
                            <div class="font-weight-bold required">{{$t('signing.headers.title')}}</div>
                            <div class="w-100">
                                <textarea
                                    class="form-control w-100"
                                    v-model="model.title"
                                    v-validate="'required'"
                                    :name="'title'+_uid"
                                    :rows="(selectedItems && selectedItems.length) || 1"
                                    :data-vv-as="$t('signing.headers.title')"
                                    :disabled="isLoadingTemplate"
                                />
                                <span class="text-danger col-12" v-if="errors.has('title'+_uid)">{{ errors.first('title'+_uid) }}</span>
                            </div>
                        </div>
                        <div class="form-group">
                            <div class="font-weight-bold required">{{$t('request_n')}}</div>
                            <div>
                                <textarea
                                    v-model="model.request"
                                    class="form-control w-100"
                                    :rows="2"
                                    :name="'textarea'+_uid"
                                    :data-vv-as="$t('request_n')"
                                    v-validate="'required'"
                                    :disabled="isLoadingTemplate"
                                />
                                <span class="text-danger col-12" v-if="errors.has('textarea'+_uid)">{{ errors.first('textarea'+_uid) }}</span>
                            </div>
                        </div>
                        <div class="form-group">
                            <div class="font-weight-bold required">{{$t('signing.headers.signatories')}}</div>
                            <Signatories
                                v-model="model.signatories"
                                api-url="api/user/searchSignatories"
                                show-all-users-btn
                                :emptyMesssage="$t('signing.noSignatoriesMsg')"
                                :remove-add-btn="(model.signatories || []).length >= 1"
                            ></Signatories>
                        </div>
                        <div class="form-group">
                            <div class="font-weight-bold">{{$t('signing.headers.deadline')}}</div>
                            <DatePicker
                                v-model="model.deadline"
                                :min="new Date()"
                                :data-vv-as="$t('signing.headers.deadline')"
                                :error="errors.first($t('signing.headers.deadline'))"
                                :fieldName="$t('signing.headers.deadline')"
                                :disabled="isLoadingTemplate"
                            ></DatePicker>
                        </div>
                        <div class="form-group">
                            <div class="font-weight-bold">{{$t('signing.attachments')}}</div>
                            <div v-if="selectedItems">
                                <div class="alert-warning">{{ $t('signing.allowedFilesTypesForSigning') }}</div>
                                <div class="mb-2" v-for="(item, index) in selectedItems" :key="index">
                                    <div>{{ item.fileName }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="submit" class="btn btn-primary" :disabled="isLoadingTemplate || !model || !(model.signatories || []).length">{{$t('buttons.save.btnText')}}</button>
                        <button type="button" class="btn btn-secondary" data-dismiss="modal">{{$t('buttons.cancel')}}</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
/* eslint-disable */
import Signatories from '@/components/coordinations/coordinators-list';
import DatePicker from '../datepicker/DateTimePicker';

import { Signing } from '@/models/models';
import signingService from '@/services/signing.service';
import { OrganizationalDocUploadTypeCode } from '@/common/documentUploadTypes';

export default {
    components: {
        Signatories,
        DatePicker
    },
    props: {
        selectedItems: { type: Array, default() { return null; } },
        eventId: { type: [String, Number], default: null },
        show: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            model: this.createEmptyModel(),
            isSaving: false,
            filesNames: [],
            showEditor: true,
            isLoadingTemplate: false
        };
    },
    methods: {
        createEmptyModel() {
            const model = new Signing();
            // Инициализиране на signatories като празен масив, за да избегнем null reference грешки
            if (!model.signatories) {
                model.signatories = [];
            }
            return model;
        },
        resetModel() {
            this.model = this.createEmptyModel();
            this.showEditor = true;
            this.isLoadingTemplate = false;
        },
        loadTemplate() {
            if (!this.eventId || !this.selectedItems || !this.selectedItems.length) {
                return;
            }

            const allowedTypes = [
                OrganizationalDocUploadTypeCode.BusinessTripOrder,
                OrganizationalDocUploadTypeCode.Order,
                OrganizationalDocUploadTypeCode.Contract
            ];

            const applicableDocs = this.selectedItems.filter(x => allowedTypes.includes(x.documentUploadType));
            if (!applicableDocs.length) {
                return;
            }

            const payload = {
                eventId: Number(this.eventId),
                organizationalDocumentIds: applicableDocs.map(x => x.organizationalDocumentId)
            };

            this.isLoadingTemplate = true;

            this.$http
                .post('/api/events/docs/signingTemplate', payload)
                .then(response => {
                    const data = response && response.data ? response.data : null;
                    if (data) {
                        if (data.title) {
                            this.model.title = data.title;
                        }
                        if (data.request) {
                            this.model.request = data.request;
                        }
                    }
                })
                .catch(err => {
                    console.error('Грешка при генериране на шаблон за подписване:', err);
                })
                .finally(() => {
                    this.isLoadingTemplate = false;
                });
        },
        onSubmit(e) {
            e.preventDefault();
            if (this.model.signatories && this.model.signatories.length) {
                this.$validator.validate().then(valid => {
                    if (valid) {
                        this.save();
                    } else {
                        console.log('Form not valid');
                    }
                });
            }
        },
        save() {
            this.isSaving = true;

            const payload = {
                title: this.model.title,
                request: this.model.request,
                deadline: this.model.deadline,
                organizationalDocuments: this.selectedItems.map(x => x.organizationalDocumentId),
                signatories: this.model.signatories.map(x => x.userId)
            };

            signingService
                .createFromOrganizationalDocuments(payload)
                .then(() => {
                    this.$vs.notify({
                        color: 'success',
                        title: this.$t('createSuccess'),
                        text: ''
                    });
                    this.$emit('updated', this.model);
                    this.hide();
                })
                .catch(err => {
                    this.$vs.notify({
                        color: 'danger',
                        title: this.$t('notifications.error'),
                        text: this.$helpers.errorText(err)
                    });
                })
                .then(() => (this.isSaving = false));
        },
        onClose() {
            this.$emit('update:show', false);
            this.$emit('onClose');
            this.isSaving = false;
            this.resetModel();
            this.showEditor = false;
            this.$validator.reset();
        },
        hide() {
            $(this.$el).modal('hide');
        },
        mountModel() {
            $('#task-type-choice').bootstrapMaterialDesign();
        },
    },
    mounted() {
        // add modal close listener
        $(this.$el).on('hidden.bs.modal', this.onClose);
        $(this.$el).on('shown.bs.modal', this.mountModel);
    },
    beforeDestroy() {
        // remove listener
        $(this.$el).off('hidden.bs.modal');
    },
    watch: {
        show: {
            handler(val) {
                this.resetModel();
                if (val) {
                    this.showEditor = true;
                    this.$nextTick(() => {
                        this.loadTemplate();
                    });
                }
            },
            immediate: false
        },
        selectedItems: {
            handler(newItems, oldItems) {
                // Проверка дали документите са се променили (нови ID-та)
                const newIds = (newItems || []).map(x => x.organizationalDocumentId).sort().join(',');
                const oldIds = (oldItems || []).map(x => x.organizationalDocumentId).sort().join(',');

                if (this.show && newIds !== oldIds) {
                    // Презареждане на шаблона при промяна на избраните документи
                    this.resetModel();
                    this.$nextTick(() => {
                        this.loadTemplate();
                    });
                }
            },
            deep: true
        }
    }
};
</script>

<style lang="scss" scoped>
.radio-message {
    padding: 3px 3px 3px 5px;
    border-left: 3px solid #03a9f4;
    box-shadow: 1px 1px 1px 1px lightgrey;
}

div.radio label {
    color: inherit;
    cursor: pointer;
}

.bmd-form-group {
    padding-top: 1rem !important;
}

.form-disabled {
    pointer-events: none;
    opacity: 0.6;
}
</style>
